import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';
// @types
import { InstructorType } from './types';
// _mock
import { countries } from '../../../_mock';
// components
import TextMaxLine from '../../../components/TextMaxLine';
import { CustomFile, UploadAvatar } from '../../../components/upload';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { isValidPhoneNumber } from 'react-phone-number-input';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
// ----------------------------------------------------------------------

interface FormValuesProps extends Omit<InstructorType, 'avatarUrl'> {
  avatarUrl: CustomFile | string | null;
}

type Props = {
  isEdit: boolean;
  currentInstructor?: InstructorType;
  getInstructorsList: any;
  handleClose: any;
};

export default function InstructorNewEditForm({
  isEdit,
  currentInstructor,
  getInstructorsList,
  handleClose,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
    const expression = /^(ftp|http|https):\/\/[^ "]+$/;
  const [country, setCountry] = useState<any>();
  // Two-mode create: 'new' provisions a login user (password), 'existing' links one.
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const NewInstructorSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email().required('Email is required'),
    phone: Yup.string(),
    country: Yup.string(),
    imageUrl: Yup.string(),
    jobTitle: Yup.string().required('Job Title is required').min(1),
    gitHubUrl: Yup.string().matches(/^https?:\/\//i, {
      message: 'Must start with https://',
      excludeEmptyString: true,
    }),
    linkedInUrl: Yup.string().matches(/^https?:\/\//i, {
      message: 'Must start with https://',
      excludeEmptyString: true,
    }),
    companyName: Yup.string().required(),
    name_ar: Yup.string(),
    jobTitle_ar: Yup.string(),
    companyName_ar: Yup.string(),
    about_ar: Yup.string(),
    keywords_ar: Yup.array(),
    companyUrl: Yup.string(),
    about: Yup.string().required('About instructor is required').min(8),
    keywords: Yup.array(),
  });

  const defaultValues = useMemo(
    () => ({
      name: currentInstructor?.name || '',
      email: currentInstructor?.email || '',
      password: '',
      phone: currentInstructor?.phone || '',
      country: currentInstructor?.country || '',
      imageUrl: currentInstructor?.imageUrl || '',
      jobTitle: currentInstructor?.jobTitle || '',
      gitHubUrl: currentInstructor?.gitHubUrl || '',
      linkedInUrl: currentInstructor?.linkedInUrl || '',
      companyUrl: currentInstructor?.companyUrl || '',
      companyName: currentInstructor?.companyName || '',
      keywords: currentInstructor?.keywords || [],
      about: currentInstructor?.about || '',
      active: currentInstructor?.active || false,
      jobTitle_ar: currentInstructor?.jobTitle_ar||'',
      name_ar: currentInstructor?.name_ar||'',
      companyName_ar: currentInstructor?.companyName_ar||'',
      about_ar: currentInstructor?.about_ar||'',
      keywords_ar: currentInstructor?.keywords_ar||[],
    }),
    [currentInstructor]
  );

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(NewInstructorSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    handleSubmit,
  setError,
    formState: { isSubmitting ,errors, },
    setValue,
    getFieldState,
  } = methods;
  const values = watch();

  useEffect(() => {
    if (isEdit && currentInstructor) {
      reset(defaultValues);
    }
    if (!isEdit) {
      reset(defaultValues);
    }
  }, [isEdit, currentInstructor]);
  const uploadImage = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
    return response.data;
  };

  // Debounced server-side user search for the "link existing user" mode.
  useEffect(() => {
    if (isEdit || mode !== 'existing') return undefined;
    const t = setTimeout(async () => {
      setUserLoading(true);
      try {
        const res = await axiosInstance.get(APIS.USERS.PAGINATED, {
          params: { page: 1, pageSize: 20, search: userInput || undefined },
        });
        const data = res.data?.data ?? res.data;
        setUserOptions(data?.items || []);
      } catch {
        setUserOptions([]);
      } finally {
        setUserLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [userInput, mode, isEdit]);

  // Auto-fill the instructor form from a picked user.
  const handlePickUser = (u: any) => {
    setSelectedUser(u);
    if (!u) return;
    setValue('name', u.name || '');
    setValue('email', u.email || '');
    if (u.phone) setValue('phone', u.phone);
    if (u.country) setValue('country', u.country);
    if (u.about) setValue('about', u.about);
    const img = u.avatar || u.avatarUrl || u.photoURL;
    if (img) setValue('imageUrl', img);
  };

  const editInstructorData = async (data: FormValuesProps) => {
    // The login password is set only on create; an edit must not send the
    // empty form value (the API refuses a password under 8 characters).
    delete (data as any).password;
    delete (data as any).userId;
    delete (data as any).mode;
    // A cleared Arabic field is sent as '' (keywords as []) so the stored
    // value is cleared too; deleting the key left the old text in place.

      if (country && values.phone) {
        const mobileValid = isValidPhoneNumber(
          values.phone.slice((country as any)?.dialCode.length, values.phone.length),
          {
            defaultCountry: String((country as any)?.countryCode).toLocaleUpperCase() as any,
            defaultCallingCode: (country as any)?.dialCode,
          }
        );
        if (!mobileValid) {
          setError('phone', {
            message: 'Wrong Phone Number',
            type: 'validate',
          });
          return;
        }
      }
    try {
        if (!data.phone && data.phone == '') {
          delete data.phone;
        }
        if (!data.country && data.country == '') {
          delete data.country;
        }
      await axiosInstance.patch(APIS.INSTRUCTORS.PATCH(currentInstructor?._id || ''), {
        ...data,
        about_ar: data?.about_ar || '',
        name_ar: data?.name_ar || '',
        keywords_ar: data?.keywords_ar || [],
        companyName_ar: data?.companyName_ar || '',
        jobTitle_ar: data?.jobTitle_ar || '',
      });
      getInstructorsList();
      reset();
      handleClose();
      enqueueSnackbar('Update success!');
    } catch (error) {
      enqueueSnackbar(error?.message || 'Unable to edit instructor!', { variant: 'error' });

      console.error(error);
    }
  };

  const AddNewInstructor = async (data: FormValuesProps) => {
     if (!data?.about_ar) delete data?.about_ar;
     if (!data?.name_ar) delete data?.name_ar;
     if (!data?.jobTitle_ar) delete data?.jobTitle_ar;
     if (!data?.companyName_ar) delete data?.companyName_ar;
     if (!data?.keywords_ar) delete data?.keywords_ar;

    // Two-mode: link an existing user, or create a new login user with a password.
    if (mode === 'existing') {
      if (!selectedUser?._id) {
        enqueueSnackbar('Please select a user to link as this instructor.', {
          variant: 'error',
        });
        return;
      }
      delete (data as any).password;
    } else if (!data.password || String(data.password).length < 8) {
      setError('password' as any, {
        message: 'A login password (min 8 characters) is required.',
        type: 'validate',
      });
      return;
    }

    if (mode === 'new' && values.phone && country)
   {   const mobileValid = isValidPhoneNumber(
        values.phone.slice((country as any)?.dialCode.length, values.phone.length),
        {
          defaultCountry: String((country as any)?.countryCode).toLocaleUpperCase() as any,
          defaultCallingCode: (country as any)?.dialCode,
        }
      );
      if (!mobileValid) {
        setError('phone', {
          message: 'Wrong Phone Number',
          type: 'validate',
        });
        return;
      }}
    try {
      data.videoUrl = data.imageUrl;
      data.active = true;
      if (!data.phone && data.phone == '')
      {
        delete data.phone;
      }
       if (!data.country && data.country == '') {
         delete data.country;
       }
        
      // SEO title/description are required by the API but not collected in the
      // form — derive sensible values from the instructor's name/about.
      await axiosInstance.post(APIS.INSTRUCTORS.ADD, {
        ...data,
        ...(mode === 'existing' ? { userId: selectedUser._id } : {}),
        title: (data as any).title || data.name,
        description: (data as any).description || data.about,
        title_ar: (data as any).title_ar || (data as any).name_ar || data.name,
        description_ar:
          (data as any).description_ar || (data as any).about_ar || data.about,
      });
      getInstructorsList();
      reset();
      handleClose();
      enqueueSnackbar('Create success!');
    } catch (err) {
      if (err?.message) {
        if (err?.message.includes('email_1 dup key'))
           enqueueSnackbar('Email is Already in use', { variant: 'error' });
          else
           enqueueSnackbar(err?.message, { variant: 'error' });
          
         
      }

      if (err?.messages.length && err?.messages[0]) {
        enqueueSnackbar(err?.messages.length && err?.messages[0], { variant: 'error' });
      } else {
        enqueueSnackbar(err?.message || 'Unable to add new instructor!', { variant: 'error' });
      }
    }
  };

  const onSubmit = async (data: FormValuesProps) => {
    isEdit ? editInstructorData(data) : AddNewInstructor(data);
  };

  return (
    <Stack height={'90%'}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <TextMaxLine marginBottom={'20px'} marginTop={'20px'} fontSize={'22px'}>
          {isEdit ? ' Edit Instructor' : ' Create Instructor'}
        </TextMaxLine>
        <Stack
          direction={'column'}
          justifyContent={'flex-start'}
          spacing={2}
          overflow={'auto'}
          height={'100%'}
        >
          {!isEdit && (
            <Stack sx={{ px: '60px' }} spacing={2}>
              <RadioGroup
                row
                value={mode}
                onChange={(e) => {
                  setMode(e.target.value as 'new' | 'existing');
                  setSelectedUser(null);
                }}
              >
                <FormControlLabel
                  value="new"
                  control={<Radio />}
                  label="Create new user account"
                />
                <FormControlLabel
                  value="existing"
                  control={<Radio />}
                  label="Link an existing user"
                />
              </RadioGroup>
              {mode === 'existing' && (
                <Autocomplete
                  options={userOptions}
                  loading={userLoading}
                  value={selectedUser}
                  onChange={(_, v) => handlePickUser(v)}
                  onInputChange={(_, v) => setUserInput(v)}
                  getOptionLabel={(o: any) =>
                    o?.name ? `${o.name} (${o.email})` : o?.email || ''
                  }
                  isOptionEqualToValue={(o: any, v: any) => o._id === v._id}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search user by name or email"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {userLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            </Stack>
          )}
          <Stack
            direction={'row'}
            pr="60px"
            pl="60px"
            alignItems={'center'}
            justifyContent={'space-between'}
            spacing={5}
          >
            <Stack alignItems={'center'} justifyContent={'center'}>
              <UploadAvatar
                error={errors.imageUrl ? true : false}
                label="avatar is required"
                accept={{ 'image/*': [] }}
                maxFiles={1}
                file={values.imageUrl ?? null}
                onDropAccepted={async (file) => {
                  let response = await uploadImage(file[0]);
                  setValue('imageUrl', response?.url);
                }}
              />
              <p style={{ color: '#FF4842', fontSize: '0.75rem', marginTop: '10px' }}>
                {errors.imageUrl?.message}
              </p>
            </Stack>
            <Stack alignItems={'center'} justifyContent={'center'}>
              <UploadAvatar
                error={errors.companyUrl ? true : false}
                label="company image"
                accept={{ 'image/*': [] }}
                maxFiles={1}
                file={values.companyUrl ?? null}
                onDropAccepted={async (file) => {
                  let response = await uploadImage(file[0]);
                  setValue('companyUrl', response?.url);
                }}
              />
              <p style={{ color: '#FF4842', fontSize: '0.75rem', marginTop: '10px' }}>
                {errors.companyUrl?.message}
              </p>
            </Stack>
          </Stack>
          <Stack>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 5,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="name" label="English Full Name" />
              <RHFTextField name="name_ar" label="Arabic Full Name" />

              <RHFTextField name="jobTitle" label="English Job Title" />
              <RHFTextField name="jobTitle_ar" label="Arabic Job Title" />
            </Box>
            <div style={{ width: '100%', marginBottom: '30px', marginTop: '30px' }}>
              <PhoneInput
                enableAreaCodes={true}
                country="ae"
                inputProps={{
                  name: 'phone',
                }}
                value={values.phone}
                onChange={(value, country) => {
                  setCountry(country);
                  setValue('phone', value);
                }}
                containerStyle={{
                  borderRadius: '8px',
                  height: '57px',
                }}
                enableSearch={false}
                inputStyle={{
                  height: '57px',
                  border: `1px solid ${errors.phone ? '#FF4842' : '#dbe0e4'}`,
                  width: '100%',
                }}
              />
              <p
                style={{
                  color: '#FF4842',
                  width: '100%',
                  fontSize: '0.75rem',
                  marginTop: '10px',
                }}
              >
                {errors?.phone && String(errors?.phone?.message)}
              </p>
            </div>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 5,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField
                autoComplete="off"
                name="email"
                type={'email'}
                label="Email Address"
                disabled={isEdit || mode === 'existing'}
              />
              <RHFSelect name="country" label="Country" placeholder="Country">
                <option value="" />
                {countries.map((option) => (
                  <option key={option.code} value={option.label}>
                    {option.label}
                  </option>
                ))}
              </RHFSelect>

              <RHFTextField name="gitHubUrl" label="GitHub Url" />
              <RHFTextField name="linkedInUrl" label="LinkedIn Url" />

              {/* <RHFTextField name="companyUrl" label="Company Image Url" /> */}
              <RHFTextField name="companyName" label="English Company Name" />
              <RHFTextField name="companyName_ar" label="Arabic Company Name" />
            </Box>
            {!isEdit && mode === 'new' && (
              <Box sx={{ mt: 3 }}>
                <RHFTextField
                  name="password"
                  type="password"
                  label="Login Password"
                  autoComplete="new-password"
                  helperText="The instructor signs in to the website with this (min 8 characters)."
                />
              </Box>
            )}
            <Stack gap={5} mt={5}>
              <RHFTextField
                name="about"
                label="English About Instructor"
                rows={4}
                minRows={4}
                multiline
              />
              <RHFTextField
                name="about_ar"
                label="Arabic About Instructor"
                rows={4}
                minRows={4}
                multiline
              />
              <div>
                <Autocomplete
                  style={{ borderColor: 'green !important' }}
                  multiple
                  id="keywords"
                  options={[]}
                  value={values.keywords}
                  freeSolo
                  renderTags={(value: string[], getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip
                        key={index}
                        variant="outlined"
                        label={option}
                        onDelete={(e) => {
                          values.keywords.splice(index, 1);

                          setValue('keywords', values.keywords);
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={errors.keywords && values.keywords.length == 0 ? true : false}
                      onKeyDown={(e) => {
                        let event = e as any;
                        if (e.key === 'Enter') {
                          setValue('keywords', [...values.keywords, event?.target?.value as any]);
                        }
                        if (e.key === 'Backspace') {
                          const value = values?.keywords;
                          value?.pop();

                          setValue('keywords', value);
                        }
                      }}
                      variant="outlined"
                      label="English Keywords"
                      placeholder="Keywords"
                      maxRows={3}
                    />
                  )}
                />
                <p style={{ color: '#FF4842', fontSize: '0.75rem', marginTop: '5px' }}>
                  {values.keywords.length == 0 && errors.keywords?.message}
                </p>
              </div>
              <div>
                <Autocomplete
                  style={{ borderColor: 'green !important' }}
                  multiple
                  id="keywords_ar"
                  options={[]}
                  value={values.keywords_ar}
                  freeSolo
                  renderTags={(value: string[], getTagProps) =>
                    value.map((option: string, index: number) => (
                      <Chip
                        key={index}
                        variant="outlined"
                        label={option}
                        onDelete={(e) => {
                          values?.keywords_ar?.splice(index, 1);
                          setValue('keywords_ar', values.keywords_ar);
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      onKeyDown={(e) => {
                        let event = e as any;
                        if (e.key === 'Enter') {
                         setValue(
                           'keywords_ar',
                           values?.keywords_ar
                             ? [...values?.keywords_ar, event?.target?.value as any]
                             : [event?.target?.value as any]
                         );
                        }
                        if (e.key === 'Backspace') {
                          const value = values?.keywords_ar;
                          value?.pop();
                           setValue('keywords_ar', value);
                        }
                      }}
                      variant="outlined"
                      label="Arabic Keywords"
                      placeholder="Keywords"
                      maxRows={3}
                    />
                  )}
                />
                
              </div>
            </Stack>
          </Stack>

          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create Instructor' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
